import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { money } from "@/lib/format";
import { X, Send, CheckCircle2, Clock, Loader2, MessageSquare } from "lucide-react";

// Bottom-sheet to review & send a chase message. Works for invoices and quotes.
export const ChaseSheet = ({ docId, onClose, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    let alive = true;
    api.post("/chase/draft", { doc_id: docId })
      .then(({ data }) => { if (!alive) return; setMsg(data.message || ""); setInfo(data); setPhone(data.client_phone || ""); })
      .catch(() => { toast.error("Couldn't draft a message"); onClose?.(); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [docId]);

  const finish = () => { onDone?.(); onClose?.(); };

  const reviewSend = async () => {
    const clean = (phone || "").replace(/\s+/g, "");
    const body = encodeURIComponent(msg);
    // If they typed a number that wasn't saved, keep it on the client for next time.
    if (clean && clean !== (info?.client_phone || "").replace(/\s+/g, "")) {
      try { await api.post("/chase/phone", { doc_id: docId, phone: phone.trim() }); } catch (e) {}
    }
    const href = clean ? `sms:${clean}?&body=${body}` : `sms:?&body=${body}`;
    if (!clean) toast.info("No number — pick a contact in Messages");
    try { window.location.href = href; } catch (e) {}
    setBusy(true);
    try { await api.post("/chase/sent", { doc_id: docId }); } catch (e) {}
    toast.success("Opening your Messages app…");
    setBusy(false);
    finish();
  };

  const markPaid = async () => {
    setBusy(true);
    try { await api.patch(`/documents/${docId}`, { status: "paid" }); toast.success("Marked as paid"); finish(); }
    catch { toast.error("Couldn't update"); } finally { setBusy(false); }
  };

  const remindLater = async () => {
    setBusy(true);
    try { await api.post("/chase/snooze", { doc_id: docId }); toast.success("We'll remind you in 3 days"); finish(); }
    catch { toast.error("Couldn't snooze"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="chase-sheet" className="fixed inset-0 z-[60] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 animate-fade-up" style={{ animationDuration: "0.15s" }} />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md bg-surface rounded-t-[24px] border-t border-border p-5 animate-fade-up" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-[10px] bg-brandTertiary flex items-center justify-center"><MessageSquare size={18} className="text-brandPrimary" /></div>
            <div>
              <p className="font-heading font-extrabold text-onSurface leading-tight">Chase {info?.client_name || "customer"}</p>
              {info && <p className="text-xs text-muted-foreground">{info.number} · {money(info.amount)}</p>}
            </div>
          </div>
          <button data-testid="chase-close" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surfaceTertiary active:scale-90 transition-transform"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground gap-2"><Loader2 size={18} className="animate-spin text-brandPrimary" /> Kai is drafting…</div>
        ) : (
          <>
            <p className="text-xs font-bold text-muted-foreground mb-1.5">Review & edit before sending</p>
            <textarea data-testid="chase-message" rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full bg-surfaceSecondary border border-border rounded-[14px] px-3.5 py-3 text-base text-onSurface focus:outline-none focus:border-brandPrimary resize-none" />
            {!info?.client_phone && (
              <div className="mt-3">
                <p className="text-xs font-bold text-muted-foreground mb-1.5">Their mobile number</p>
                <input data-testid="chase-phone-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 07700 900123 (or leave blank to skip)" className="w-full bg-surfaceSecondary border border-border rounded-[14px] px-3.5 py-3 text-base text-onSurface focus:outline-none focus:border-brandPrimary" />
                <p className="text-xs text-muted-foreground mt-1">No number saved for this customer. Add one to text them, or leave blank and pick a contact in Messages.</p>
              </div>
            )}

            <button data-testid="chase-review-send" disabled={busy || !msg.trim()} onClick={reviewSend} className="w-full mt-4 bg-brandPrimary text-onBrand rounded-full h-13 py-3.5 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50">
              <Send size={18} /> Review & Send
            </button>
            <div className="flex gap-2 mt-2">
              {info?.kind === "invoice" && (
                <button data-testid="chase-mark-paid" disabled={busy} onClick={markPaid} className="flex-1 bg-surface border border-border rounded-full h-11 text-sm font-semibold text-success flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"><CheckCircle2 size={16} /> Mark as Paid</button>
              )}
              <button data-testid="chase-remind-later" disabled={busy} onClick={remindLater} className="flex-1 bg-surface border border-border rounded-full h-11 text-sm font-semibold text-onSurface flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"><Clock size={16} /> Remind Me Later</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
